import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, CircleHelp, Compass } from 'lucide-react';
import {
  advanceTourProgress,
  finishTourProgress,
  pendingTourSteps,
  readTourProgress,
  saveTourProgress,
  startTourProgress,
  type TourProgress,
} from '../../utils/panelTour';

export interface PanelTourStep {
  id: string;
  title: string;
  description: string;
  target: string;
  fallbackTarget?: string;
  tab?: string;
  query?: Record<string, string>;
}

interface PanelTourProps {
  tourId: string;
  title: string;
  steps: PanelTourStep[];
  enabled: boolean;
  onStart: () => void;
  onNavigate: (step: PanelTourStep) => void;
  onEnd: () => void;
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}
interface Layout {
  width: number;
  height: number;
  offsetTop: number;
  offsetLeft: number;
  target: Box | null;
  panel: Box;
}

export function PanelTour(props: PanelTourProps) {
  const latest = useRef(props);
  latest.current = props;
  const [progress, setProgress] = useState<TourProgress | null>(null);
  const [active, setActive] = useState(false);
  const [runIds, setRunIds] = useState<string[]>([]);
  const [layout, setLayout] = useState<Layout | null>(null);
  const progressRef = useRef(progress);
  const portalRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const replayRef = useRef<HTMLButtonElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const originalScroll = useRef<
    { element: Element; top: number; left: number }[]
  >([]);
  const titleId = useId();
  const descriptionId = useId();
  const steps = props.steps.filter((s) => runIds.includes(s.id));
  const availableKey = props.steps.map((s) => s.id).join('|');
  const stepIndex = Math.max(
    0,
    steps.findIndex((s) => s.id === progress?.stepId),
  );
  const step = steps[stepIndex];

  const persist = (next: TourProgress) => {
    progressRef.current = next;
    setProgress(next);
    saveTourProgress(latest.current.tourId, next);
  };
  const start = (replay: boolean) => {
    const { steps: available, tourId, onStart } = latest.current;
    const previous = readTourProgress(tourId);
    const ids = replay ? available.map((s) => s.id) : pendingTourSteps(available.map((s) => s.id), previous);
    const steps = available.filter((s) => ids.includes(s.id));
    if (!steps.length) return;
    setRunIds(ids);
    const first =
      !replay && previous?.status === 'in_progress'
        ? (steps.find((s) => s.id === previous.stepId) ?? steps[0])
        : steps[0];
    previouslyFocused.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    originalScroll.current = Array.from(
      document.querySelectorAll('main, [data-tour-scroll]'),
    ).map((element) => ({
      element,
      top: element.scrollTop,
      left: element.scrollLeft,
    }));
    onStart();
    persist({ ...startTourProgress(first.id, previous, replay), runSteps: ids });
    setActive(true);
  };
  const finish = (status: 'completed' | 'skipped') => {
    if (progressRef.current)
      persist(finishTourProgress(progressRef.current, status, steps.map((s) => s.id)));
    setActive(false);
    latest.current.onEnd();
    requestAnimationFrame(() => {
      originalScroll.current.forEach(({ element, top, left }) =>
        element.scrollTo({ top, left, behavior: 'instant' }),
      );
      const target = previouslyFocused.current;
      (target?.isConnected && target !== document.body
        ? target
        : replayRef.current
      )?.focus({ preventScroll: true });
    });
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;

  useEffect(() => {
    if (!props.enabled || active) return;
    // Let authentication and any existing welcome/confidentiality modal settle first.
    const timer = window.setInterval(() => {
      if (
        document.visibilityState !== 'visible' ||
        document.querySelector('[aria-modal="true"], .modal-overlay')
      )
        return;
      clearInterval(timer);
      start(false);
    }, 500);
    return () => clearInterval(timer);
  }, [props.enabled, props.tourId, availableKey, active]);

  useEffect(() => {
    // A permission revocation must immediately remove the step and release the UI.
    if (active && (!props.enabled || !steps.length)) finishRef.current('completed');
  }, [active, props.enabled, steps.length]);

  useEffect(() => {
    if (!active || !step) return;
    latest.current.onNavigate(step);
    setLayout(null);
    if (cardRef.current) cardRef.current.scrollTop = 0;
    let frame = 0;
    let target: HTMLElement | null = null;
    let scrolled = false;
    let placed = false;
    const started = performance.now();
    const measure = () => {
      const view = window.visualViewport;
      const width = view?.width ?? window.innerWidth;
      const height = view?.height ?? window.innerHeight;
      const offsetTop = view?.offsetTop ?? 0;
      const offsetLeft = view?.offsetLeft ?? 0;
      const mobile = width < 768;
      const panelWidth = Math.min(380, width - 24);
      const panelHeight = Math.min(
        cardRef.current?.offsetHeight ?? 280,
        height - 24,
      );
      const found = document.querySelector<HTMLElement>(step.target);
      const visible = found && found.getClientRects().length > 0;
      target = visible
        ? found
        : performance.now() - started > 1200 && step.fallbackTarget
          ? document.querySelector<HTMLElement>(step.fallbackTarget)
          : null;
      if (target && !scrolled) {
        target.scrollIntoView({
          block: 'center',
          inline: 'nearest',
          behavior: 'instant',
        });
        scrolled = true;
      }
      if (target && mobile && !placed) {
        const bottom = target.getBoundingClientRect().bottom - offsetTop;
        const availableBottom = height - panelHeight - 36;
        if (bottom > availableBottom) {
          let parent = target.parentElement;
          while (parent) {
            if (
              /(auto|scroll)/.test(getComputedStyle(parent).overflowY) &&
              parent.scrollHeight > parent.clientHeight
            ) {
              parent.scrollTop += bottom - availableBottom;
              break;
            }
            parent = parent.parentElement;
          }
        }
        placed = true;
      }
      let box: Box | null = null;
      if (target) {
        const rect = target.getBoundingClientRect();
        let left = Math.max(8, rect.left - offsetLeft - 6);
        let top = Math.max(8, rect.top - offsetTop - 6);
        let right = Math.min(width - 8, rect.right - offsetLeft + 6);
        let bottom = Math.min(height - 8, rect.bottom - offsetTop + 6);
        // Clip the spotlight to scroll containers, not over sticky app chrome.
        for (
          let parent = target.parentElement;
          parent;
          parent = parent.parentElement
        ) {
          if (/(auto|scroll|hidden)/.test(getComputedStyle(parent).overflowY)) {
            const clip = parent.getBoundingClientRect();
            top = Math.max(top, clip.top - offsetTop);
            bottom = Math.min(bottom, clip.bottom - offsetTop);
            left = Math.max(left, clip.left - offsetLeft);
            right = Math.min(right, clip.right - offsetLeft);
          }
        }
        if (right > left && bottom > top)
          box = { left, top, width: right - left, height: bottom - top };
      }
      let panelTop = mobile
        ? height - panelHeight - 12
        : (height - panelHeight) / 2;
      let panelLeft = (width - panelWidth) / 2;
      if (!mobile && box) {
        panelLeft = Math.min(width - panelWidth - 12, Math.max(12, box.left));
        if (box.top + box.height + panelHeight + 24 <= height)
          panelTop = box.top + box.height + 16;
        else if (box.top >= panelHeight + 24)
          panelTop = box.top - panelHeight - 16;
        else if (box.left + box.width + panelWidth + 24 < width)
          panelLeft = box.left + box.width + 16;
      }
      const next = {
        width,
        height,
        offsetTop,
        offsetLeft,
        target: box,
        panel: {
          left: panelLeft,
          top: Math.max(12, panelTop),
          width: panelWidth,
          height: panelHeight,
        },
      };
      setLayout((previous) =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
      );
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = new MutationObserver(schedule);
    observer.observe(document.getElementById('root') ?? document.body, {
      childList: true,
      subtree: true,
    });
    const resize = new ResizeObserver(() => {
      placed = false;
      schedule();
    });
    if (cardRef.current) resize.observe(cardRef.current);
    resize.observe(document.documentElement);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);
    const retry = window.setInterval(schedule, 250);
    schedule();
    headingRef.current?.focus({ preventScroll: true });
    return () => {
      cancelAnimationFrame(frame);
      clearInterval(retry);
      observer.disconnect();
      resize.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
    };
  }, [active, step?.id]);

  useEffect(() => {
    if (!active) return;
    const siblings = Array.from(document.body.children).filter(
      (el) => el !== portalRef.current,
    );
    const previous = siblings.map((el) => ({
      el,
      inert: el.hasAttribute('inert'),
    }));
    siblings.forEach((el) => el.setAttribute('inert', ''));
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        finishRef.current('skipped');
      }
      if (event.key === 'Tab') {
        const buttons = Array.from(
          cardRef.current?.querySelectorAll<HTMLButtonElement>(
            'button:not(:disabled)',
          ) ?? [],
        );
        if (!buttons.length) return;
        const index = buttons.indexOf(
          document.activeElement as HTMLButtonElement,
        );
        event.preventDefault();
        buttons[
          event.shiftKey
            ? index <= 0
              ? buttons.length - 1
              : index - 1
            : (index + 1) % buttons.length
        ].focus();
      }
    };
    const keepFocus = (event: FocusEvent) => {
      if (!cardRef.current?.contains(event.target as Node))
        headingRef.current?.focus({ preventScroll: true });
    };
    document.addEventListener('keydown', keydown, true);
    document.addEventListener('focusin', keepFocus);
    return () => {
      previous.forEach(({ el, inert }) => {
        if (!inert) el.removeAttribute('inert');
      });
      document.removeEventListener('keydown', keydown, true);
      document.removeEventListener('focusin', keepFocus);
    };
  }, [active]);

  if (!props.enabled || !props.steps.length) return null;
  const box = layout?.target;
  return (
    <>
      <button
        ref={replayRef}
        data-tour={`${props.tourId}-replay`}
        type="button"
        onClick={() => start(true)}
        className="glass-button-secondary min-h-[44px]"
        aria-label={`Take a tour of ${props.title}`}
      >
        <CircleHelp className="w-4 h-4" />
        Take a tour
      </button>
      {active && step &&
        createPortal(
          <div
            ref={portalRef}
            data-panel-tour={props.tourId}
            className="fixed inset-0 z-[12000] overscroll-contain"
            style={{ touchAction: 'none' }}
          >
            <svg
              className="absolute pointer-events-none"
              style={{
                left: layout?.offsetLeft ?? 0,
                top: layout?.offsetTop ?? 0,
                width: layout?.width ?? '100%',
                height: layout?.height ?? '100%',
              }}
              aria-hidden="true"
            >
              {layout ? (
                <path
                  fill="var(--bg-overlay)"
                  fillRule="evenodd"
                  d={`M0 0H${layout.width}V${layout.height}H0Z${box ? ` M${box.left} ${box.top}v${box.height}h${box.width}v-${box.height}Z` : ''}`}
                />
              ) : (
                <rect width="100%" height="100%" fill="var(--bg-overlay)" />
              )}
            </svg>
            {box && (
              <div
                aria-hidden="true"
                className="absolute pointer-events-none rounded-lg border-2 border-accent-400"
                style={{
                  left: box.left + (layout?.offsetLeft ?? 0),
                  top: box.top + (layout?.offsetTop ?? 0),
                  width: box.width,
                  height: box.height,
                }}
              />
            )}
            <div
              ref={cardRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              className="modal-panel absolute !m-0 !p-5 shadow-2xl overflow-y-auto"
              style={{
                background: 'var(--bg-elevated)',
                touchAction: 'pan-y',
                left: layout ? layout.panel.left + layout.offsetLeft : 12,
                top: layout ? layout.panel.top + layout.offsetTop : 12,
                width: layout?.panel.width ?? 'min(380px, calc(100vw - 24px))',
                maxHeight: layout ? layout.height - 24 : 'calc(100dvh - 24px)',
              }}
            >
              <div
                className="sticky top-0 z-10 flex items-center justify-between gap-3 mb-4"
                style={{ background: 'var(--bg-elevated)' }}
              >
                <span
                  className="eyebrow flex items-center gap-2"
                  style={{ color: 'var(--text-muted)' }}
                >
                  <Compass className="w-4 h-4 text-accent-400" />
                  {props.title} tour
                </span>
                <button
                  type="button"
                  className="glass-button-secondary min-h-[44px]"
                  style={{ color: 'var(--text-primary)' }}
                  onClick={() => finish('skipped')}
                >
                  Skip tour
                </button>
              </div>
              <p
                className="text-xs mb-2"
                style={{ color: 'var(--text-muted)' }}
              >
                Step {stepIndex + 1} of {steps.length}
              </p>
              <h2
                ref={headingRef}
                id={titleId}
                tabIndex={-1}
                className="text-lg font-semibold text-white outline-none"
              >
                {step.title}
              </h2>
              <p
                id={descriptionId}
                className="text-sm leading-relaxed mt-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                {step.description}
              </p>
              <div aria-hidden="true" className="flex gap-1 mt-5">
                {steps.map((item, index) => (
                  <span
                    key={item.id}
                    className={`h-1 rounded-full flex-1 ${index <= stepIndex ? 'bg-accent-400' : 'bg-glass-200/20'}`}
                  />
                ))}
              </div>
              <div className="flex items-center justify-between gap-3 mt-4">
                <button
                  type="button"
                  className="glass-button-secondary min-h-[44px]"
                  disabled={stepIndex === 0}
                  onClick={() => {
                    if (progressRef.current)
                      persist(
                        advanceTourProgress(
                          progressRef.current,
                          steps[stepIndex - 1].id,
                        ),
                      );
                  }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary min-h-[44px]"
                  onClick={() => {
                    if (stepIndex === steps.length - 1)
                      finish('completed');
                    else if (progressRef.current)
                      persist(
                        advanceTourProgress(
                          progressRef.current,
                          steps[stepIndex + 1].id,
                        ),
                      );
                  }}
                >
                  {stepIndex === steps.length - 1
                    ? 'Finish tour'
                    : 'Next'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
